import React, { useEffect, useState } from 'react'
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Alert,
  Platform,
  Share,
  Linking } from 'react-native'
import { ArrowLeft, ChevronRight, Tag, Share2, FileSpreadsheet, CreditCard, Check, AlertTriangle } from 'lucide-react-native'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import { useAuth } from '../contexts/AuthContext'
import { startSellerConnectOnboarding } from '../lib/stripe'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import theme from '../theme'
import {
  Button,
  ButtonText,
  Card,
  Spinner,
  TopBar,
  XStack,
  YStack } from '@fliisi/pollen'

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'ReservationSettings'>
  route: RouteProp<RootStackParamList, 'ReservationSettings'>
}

interface Reservation {
  id: string
  start_date: string
  end_date: string
  status: string
  payment_status: string
  total_price: number | null
  space: {
    id: string
    name: string
    space_type: string
    price_per_day: number
  }
  market: {
    id: string
    name: string
    commission_percentage: number
  }
}

interface ProductStats {
  total: number
  for_sale: number
  sold: number
  total_value: number
  sold_value: number
}

interface PayoutRecord {
  id: string
  gross_amount: number
  commission_amount: number
  net_amount: number
  commission_rate: number
  status: string
  items_count: number
  created_at: string
  market?: {
    name: string
  }
}

export default function ReservationSettingsScreen({ navigation, route }: Props) {
  const { reservationId } = route.params
  const { t, language } = useLanguage()
  const { user } = useAuth()
  const [reservation, setReservation] = useState<Reservation | null>(null)
  const [productStats, setProductStats] = useState<ProductStats>({
    total: 0,
    for_sale: 0,
    sold: 0,
    total_value: 0,
    sold_value: 0 })
  const [loading, setLoading] = useState(true)
  const [cancelling, setCancelling] = useState(false)
  const [connectingStripe, setConnectingStripe] = useState(false)
  const [sellerStripeConnected, setSellerStripeConnected] = useState(false)
  const [payoutHistory, setPayoutHistory] = useState<PayoutRecord[]>([])

  useEffect(() => {
    fetchData()
  }, [reservationId])

  const fetchData = async () => {
    setLoading(true)
    try {
      // Fetch reservation details
      const { data: resData, error: resError } = await supabase
        .from('space_reservations')
        .select(`
          id,
          start_date,
          end_date,
          status,
          payment_status,
          price_total,
          space:market_spaces (
            id,
            name,
            space_type,
            price_per_day
          ),
          market:flea_markets (
            id,
            name,
            commission_percentage
          )
        `)
        .eq('id', reservationId)
        .single()

      if (resError) {
        console.error('Reservation fetch error:', resError)
        throw resError
      }

      setReservation({
        id: resData.id,
        start_date: resData.start_date,
        end_date: resData.end_date,
        status: resData.status,
        payment_status: resData.payment_status || 'unpaid',
        total_price: resData.price_total,
        space: Array.isArray(resData.space) ? resData.space[0] : resData.space,
        market: Array.isArray(resData.market) ? resData.market[0] : resData.market })

      // Fetch product statistics
      const { data: prodData, error: prodError } = await supabase
        .from('products')
        .select('id, price, status')
        .eq('reservation_id', reservationId)

      if (!prodError && prodData) {
        const stats: ProductStats = {
          total: prodData.length,
          for_sale: 0,
          sold: 0,
          total_value: 0,
          sold_value: 0 }

        prodData.forEach((p) => {
          stats.total_value += p.price || 0
          if (p.status === 'for_sale') {
            stats.for_sale++
          } else if (p.status === 'sold') {
            stats.sold++
            stats.sold_value += p.price || 0
          }
        })

        setProductStats(stats)
      }

      // Check if seller has Stripe connected and fetch payout history
      if (user?.id) {
        const { data: userData } = await supabase
          .from('users')
          .select('stripe_connect_account_id')
          .eq('id', user.id)
          .single()

        setSellerStripeConnected(!!userData?.stripe_connect_account_id)

        // Fetch seller's payout history
        const { data: payouts } = await supabase
          .from('payouts')
          .select(`
            id,
            gross_amount,
            commission_amount,
            net_amount,
            commission_rate,
            status,
            items_count,
            created_at,
            market:flea_markets (
              name
            )
          `)
          .eq('seller_id', user.id)
          .order('created_at', { ascending: false })
          .limit(10)

        if (payouts) {
          const formattedPayouts = payouts.map(p => ({
            ...p,
            market: Array.isArray(p.market) ? p.market[0] : p.market }))
          setPayoutHistory(formattedPayouts)
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }
    const locale = language === 'fi' ? 'fi-FI' : 'en-US'
    return date.toLocaleDateString(locale, options)
  }

  const getDaysRemaining = () => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const endDate = new Date(reservation?.end_date || '')
    endDate.setHours(0, 0, 0, 0)
    const diffTime = endDate.getTime() - today.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  const handlePrintTags = () => {
    navigation.navigate('PrintTags', { reservationId })
  }

  const handleShareProducts = async () => {
    const baseUrl = 'https://kirpputori.dev.sakualy.ai'
    const shareUrl = `${baseUrl}/booth/${user?.id}/${reservationId}`
    const message = `${t('check_out_my_products') || 'Check out my products at'} ${reservation?.market?.name}: ${shareUrl}`

    if (Platform.OS === 'web') {
      if (navigator.share) {
        try {
          await navigator.share({ title: reservation?.market?.name, text: message, url: shareUrl })
        } catch (e) {
          // User cancelled or error
          navigator.clipboard?.writeText(shareUrl)
          window.alert(t('link_copied') || 'Link copied to clipboard!')
        }
      } else {
        navigator.clipboard?.writeText(shareUrl)
        window.alert(t('link_copied') || 'Link copied to clipboard!')
      }
    } else {
      try {
        await Share.share({ message })
      } catch (e) {
        console.error('Error sharing:', e)
      }
    }
  }

  const handleExportCSV = () => {
    // Generate CSV content
    const exportProducts = async () => {
      const { data: products, error } = await supabase
        .from('products')
        .select('id, title, title_fi, title_en, price, status, created_at')
        .eq('reservation_id', reservationId)
        .order('created_at')

      if (error || !products) {
        const msg = t('export_error') || 'Failed to export products'
        Platform.OS === 'web' ? window.alert(msg) : Alert.alert(t('error'), msg)
        return
      }

      const headers = ['ID', 'Title', 'Title (FI)', 'Title (EN)', 'Price', 'Status', 'Created']
      const rows = products.map((p) => [
        p.id,
        `"${(p.title || '').replace(/"/g, '""')}"`,
        `"${(p.title_fi || '').replace(/"/g, '""')}"`,
        `"${(p.title_en || '').replace(/"/g, '""')}"`,
        p.price,
        p.status,
        p.created_at,
      ])

      const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n')

      if (Platform.OS === 'web') {
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `products-${reservationId.slice(-6)}.csv`
        link.click()
      } else {
        // For native, would need file system access
        const msg = t('csv_export_web_only') || 'CSV export is currently only available on web'
        Alert.alert(t('info'), msg)
      }
    }

    exportProducts()
  }

  const handleConnectBankAccount = async () => {
    setConnectingStripe(true)
    try {
      const result = await startSellerConnectOnboarding()

      if (result.error) {
        const msg = result.error
        Platform.OS === 'web' ? window.alert(msg) : Alert.alert(t('error') || 'Error', msg)
        return
      }

      if (result.url) {
        if (Platform.OS === 'web') {
          window.open(result.url, '_blank')
        } else {
          await Linking.openURL(result.url)
        }
      }
    } catch (error: any) {
      console.error('Stripe connect error:', error)
      const msg = error.message || 'Failed to connect bank account'
      Platform.OS === 'web' ? window.alert(msg) : Alert.alert(t('error') || 'Error', msg)
    } finally {
      setConnectingStripe(false)
    }
  }

  const handleCancelReservation = () => {
    const confirmCancel = async () => {
      setCancelling(true)
      try {
        // First unlink all products
        const { error: unlinkError } = await supabase
          .from('products')
          .update({ reservation_id: null, status: 'draft' })
          .eq('reservation_id', reservationId)

        if (unlinkError) throw unlinkError

        // Then cancel the reservation
        const { error: cancelError } = await supabase
          .from('space_reservations')
          .update({ status: 'cancelled' })
          .eq('id', reservationId)
          .eq('seller_id', user?.id)

        if (cancelError) throw cancelError

        const msg = t('reservation_cancelled') || 'Reservation cancelled successfully'
        if (Platform.OS === 'web') {
          window.alert(msg)
        } else {
          Alert.alert(t('success'), msg)
        }
        navigation.goBack()
      } catch (error) {
        console.error('Error cancelling reservation:', error)
        const msg = t('cancel_error') || 'Failed to cancel reservation'
        Platform.OS === 'web' ? window.alert(msg) : Alert.alert(t('error'), msg)
      } finally {
        setCancelling(false)
      }
    }

    const msg = t('cancel_reservation_confirm') || 'Are you sure you want to cancel this reservation? All linked products will be unlinked.'

    if (Platform.OS === 'web') {
      if (window.confirm(msg)) {
        confirmCancel()
      }
    } else {
      Alert.alert(
        t('cancel_reservation') || 'Cancel Reservation',
        msg,
        [
          { text: t('no') || 'No', style: 'cancel' },
          { text: t('yes') || 'Yes', style: 'destructive', onPress: confirmCancel },
        ]
      )
    }
  }

  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
            <ArrowLeft size={18} color={theme.colors.brand.primary} />
            <Text style={styles.backButtonText}>{t('back') || 'Back'}</Text>
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{t('manage_reservation') || 'Manage Reservation'}</Text>
          <View style={styles.headerSpacer} />
        </View>
        <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack>
      </View>
    )
  }

  const daysRemaining = getDaysRemaining()

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <ArrowLeft size={18} color={theme.colors.brand.primary} />
          <Text style={styles.backButtonText}>{t('back') || 'Back'}</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t('manage_reservation') || 'Manage Reservation'}</Text>
        <View style={styles.headerSpacer} />
      </View>

      <ScrollView style={styles.content}>
        {/* Reservation Info Card */}
        <View style={styles.infoCard}>
          <View style={styles.infoHeader}>
            <View>
              <Text style={styles.spaceName}>{reservation?.space?.name}</Text>
              <Text style={styles.marketName}>{reservation?.market?.name}</Text>
            </View>
            <View style={[
              styles.statusBadge,
              { backgroundColor: reservation?.status === 'confirmed' ? '#4CAF50' : '#FF9800' }
            ]}>
              <Text style={styles.statusText}>{t(reservation?.status || '') || reservation?.status}</Text>
            </View>
          </View>

          <View style={styles.dateRow}>
            <View style={styles.dateItem}>
              <Text style={styles.dateLabel}>{t('start_date') || 'Start'}</Text>
              <Text style={styles.dateValue}>{formatDate(reservation?.start_date || '')}</Text>
            </View>
            <View style={styles.dateDivider} />
            <View style={styles.dateItem}>
              <Text style={styles.dateLabel}>{t('end_date') || 'End'}</Text>
              <Text style={styles.dateValue}>{formatDate(reservation?.end_date || '')}</Text>
            </View>
          </View>

          {daysRemaining > 0 && (
            <View style={styles.daysRemainingBadge}>
              <Text style={styles.daysRemainingText}>
                {daysRemaining} {t('days_remaining') || 'days remaining'}
              </Text>
            </View>
          )}
        </View>

        {/* Product Statistics */}
        <View style={styles.statsCard}>
          <Text style={styles.sectionTitle}>{t('product_statistics') || 'Product Statistics'}</Text>

          <View style={styles.statsGrid}>
            <View style={styles.statItem}>
              <Text style={styles.statValue}>{productStats.total}</Text>
              <Text style={styles.statLabel}>{t('total_products') || 'Total'}</Text>
            </View>
            <View style={styles.statItem}>
              <Text style={[styles.statValue, { color: '#4CAF50' }]}>{productStats.for_sale}</Text>
              <Text style={styles.statLabel}>{t('for_sale') || 'For Sale'}</Text>
            </View>
            <View style={styles.statItem}>
              <Text style={[styles.statValue, { color: '#2196F3' }]}>{productStats.sold}</Text>
              <Text style={styles.statLabel}>{t('sold') || 'Sold'}</Text>
            </View>
          </View>

          <View style={styles.valueSummary}>
            <View style={styles.valueRow}>
              <Text style={styles.valueLabel}>{t('total_value') || 'Total Value'}</Text>
              <Text style={styles.valueAmount}>€{productStats.total_value.toFixed(2)}</Text>
            </View>
            <View style={styles.valueRow}>
              <Text style={styles.valueLabel}>{t('sold_value') || 'Sold Value'}</Text>
              <Text style={[styles.valueAmount, { color: '#4CAF50' }]}>€{productStats.sold_value.toFixed(2)}</Text>
            </View>
            {reservation?.market?.commission_percentage && productStats.sold_value > 0 && (
              <View style={styles.valueRow}>
                <Text style={styles.valueLabel}>
                  {t('commission') || 'Commission'} ({reservation.market.commission_percentage}%)
                </Text>
                <Text style={[styles.valueAmount, { color: '#F44336' }]}>
                  -€{(productStats.sold_value * reservation.market.commission_percentage / 100).toFixed(2)}
                </Text>
              </View>
            )}
          </View>
        </View>

        {/* Payment Section - Show if unpaid */}
        {reservation?.payment_status !== 'paid' && reservation?.total_price && reservation.total_price > 0 && (
          <View style={styles.paymentSection}>
            <View style={styles.paymentHeader}>
              <Text style={styles.paymentLabel}>{t('payment_status') || 'Payment Status'}</Text>
              <View style={styles.unpaidBadge}>
                <Text style={styles.unpaidBadgeText}>{t('unpaid') || 'Unpaid'}</Text>
              </View>
            </View>
            <Text style={styles.paymentAmount}>€{reservation.total_price.toFixed(2)}</Text>
            <TouchableOpacity
              style={styles.payNowButton}
              onPress={() => navigation.navigate('Checkout', { reservationId })}
            >
              <View style={styles.payNowButtonContent}>
                <CreditCard size={18} color="#fff" />
                <Text style={styles.payNowButtonText}>{t('pay_now') || 'Pay Now'}</Text>
              </View>
            </TouchableOpacity>
          </View>
        )}

        {/* Payout Setup Section */}
        <View style={styles.payoutSetupSection}>
          <Text style={styles.sectionTitle}>{t('payout_setup') || 'Payout Setup'}</Text>

          {sellerStripeConnected ? (
            <View style={styles.payoutConnected}>
              <View style={styles.payoutConnectedHeader}>
                <Check size={20} color="#4CAF50" />
                <Text style={styles.payoutConnectedTitle}>
                  {t('bank_account_connected') || 'Bank Account Connected'}
                </Text>
              </View>
              <Text style={styles.payoutConnectedDesc}>
                {t('payout_ready_desc') || 'You can receive automatic payouts when your items sell.'}
              </Text>
            </View>
          ) : (
            <View style={styles.payoutNotConnected}>
              <Text style={styles.payoutSetupDesc}>
                {t('payout_setup_desc') || 'Connect your bank account to receive automatic payouts when your items sell. Without this, the market will need to pay you manually.'}
              </Text>
              <View style={styles.payoutBenefits}>
                <Text style={styles.payoutBenefit}>• {t('automatic_payouts') || 'Automatic payouts to your bank'}</Text>
                <Text style={styles.payoutBenefit}>• {t('fast_transfers') || 'Fast transfers (1-2 business days)'}</Text>
                <Text style={styles.payoutBenefit}>• {t('secure_payments') || 'Secure and encrypted'}</Text>
              </View>
              <TouchableOpacity
                style={styles.connectBankButton}
                onPress={handleConnectBankAccount}
                disabled={connectingStripe}
              >
                {connectingStripe ? (
                  <Spinner color="#fff" />
                ) : (
                  <Text style={styles.connectBankButtonText}>
                    🏦 {t('connect_bank_account') || 'Connect Bank Account'}
                  </Text>
                )}
              </TouchableOpacity>
            </View>
          )}
        </View>

        {/* Payout History */}
        {payoutHistory.length > 0 && (
          <View style={styles.payoutHistorySection}>
            <Text style={styles.sectionTitle}>{t('payout_history') || 'Payout History'}</Text>

            {payoutHistory.map((payout) => (
              <View key={payout.id} style={styles.payoutHistoryCard}>
                <View style={styles.payoutHistoryHeader}>
                  <View>
                    <Text style={styles.payoutHistoryMarket}>
                      {payout.market?.name || 'Unknown Market'}
                    </Text>
                    <Text style={styles.payoutHistoryDate}>
                      {new Date(payout.created_at).toLocaleDateString(language === 'fi' ? 'fi-FI' : 'en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric' })}
                    </Text>
                  </View>
                  <View style={[
                    styles.payoutHistoryBadge,
                    payout.status === 'completed' ? styles.payoutHistoryBadgeSuccess : styles.payoutHistoryBadgePending
                  ]}>
                    <Text style={[
                      styles.payoutHistoryBadgeText,
                      payout.status === 'completed' ? styles.payoutHistoryBadgeTextSuccess : styles.payoutHistoryBadgeTextPending
                    ]}>
                      {payout.status === 'completed' ? (t('paid') || 'Paid') : payout.status}
                    </Text>
                  </View>
                </View>
                <View style={styles.payoutHistoryDetails}>
                  <View style={styles.payoutHistoryRow}>
                    <Text style={styles.payoutHistoryLabel}>{t('items_sold') || 'Items sold'}</Text>
                    <Text style={styles.payoutHistoryValue}>{payout.items_count}</Text>
                  </View>
                  <View style={styles.payoutHistoryRow}>
                    <Text style={styles.payoutHistoryLabel}>{t('gross') || 'Gross'}</Text>
                    <Text style={styles.payoutHistoryValue}>€{payout.gross_amount.toFixed(2)}</Text>
                  </View>
                  <View style={styles.payoutHistoryRow}>
                    <Text style={styles.payoutHistoryLabel}>{t('commission') || 'Commission'}</Text>
                    <Text style={styles.payoutHistoryValueMuted}>-€{payout.commission_amount.toFixed(2)}</Text>
                  </View>
                  <View style={[styles.payoutHistoryRow, styles.payoutHistoryRowTotal]}>
                    <Text style={styles.payoutHistoryLabelBold}>{t('received') || 'Received'}</Text>
                    <Text style={styles.payoutHistoryValueBold}>€{payout.net_amount.toFixed(2)}</Text>
                  </View>
                </View>
              </View>
            ))}
          </View>
        )}

        {/* Actions */}
        <View style={styles.actionsSection}>
          <Text style={styles.sectionTitle}>{t('actions') || 'Actions'}</Text>

          <TouchableOpacity style={styles.actionButton} onPress={handlePrintTags}>
            <View style={styles.actionIconContainer}>
              <Tag size={20} color={theme.colors.brand.primary} />
            </View>
            <View style={styles.actionInfo}>
              <Text style={styles.actionTitle}>{t('print_price_tags') || 'Print Price Tags'}</Text>
              <Text style={styles.actionDescription}>
                {t('print_tags_desc') || 'Generate printable labels with QR codes'}
              </Text>
            </View>
            <ChevronRight size={20} color={theme.colors.text.secondary} />
          </TouchableOpacity>

          <TouchableOpacity style={styles.actionButton} onPress={handleShareProducts}>
            <View style={styles.actionIconContainer}>
              <Share2 size={20} color={theme.colors.brand.primary} />
            </View>
            <View style={styles.actionInfo}>
              <Text style={styles.actionTitle}>{t('share_products') || 'Share Products'}</Text>
              <Text style={styles.actionDescription}>
                {t('share_products_desc') || 'Get a shareable link to your products'}
              </Text>
            </View>
            <ChevronRight size={20} color={theme.colors.text.secondary} />
          </TouchableOpacity>

          <TouchableOpacity style={styles.actionButton} onPress={handleExportCSV}>
            <View style={styles.actionIconContainer}>
              <FileSpreadsheet size={20} color={theme.colors.brand.primary} />
            </View>
            <View style={styles.actionInfo}>
              <Text style={styles.actionTitle}>{t('export_csv') || 'Export to CSV'}</Text>
              <Text style={styles.actionDescription}>
                {t('export_csv_desc') || 'Download product list as spreadsheet'}
              </Text>
            </View>
            <ChevronRight size={20} color={theme.colors.text.secondary} />
          </TouchableOpacity>
        </View>

        {/* Danger Zone */}
        <View style={styles.dangerSection}>
          <Text style={[styles.sectionTitle, { color: '#F44336' }]}>
            {t('danger_zone') || 'Danger Zone'}
          </Text>

          <TouchableOpacity
            style={styles.dangerButton}
            onPress={handleCancelReservation}
            disabled={cancelling}
          >
            {cancelling ? (
              <Spinner size="small" />
            ) : (
              <>
                <View style={styles.dangerIconContainer}>
                  <AlertTriangle size={20} color="#F44336" />
                </View>
                <View style={styles.actionInfo}>
                  <Text style={styles.dangerTitle}>{t('cancel_reservation') || 'Cancel Reservation'}</Text>
                  <Text style={styles.dangerDescription}>
                    {t('cancel_reservation_desc') || 'This will unlink all products and cancel your booking'}
                  </Text>
                </View>
              </>
            )}
          </TouchableOpacity>
        </View>

        <View style={styles.bottomPadding} />
      </ScrollView>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.primary },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center' },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.md,
    paddingTop: 16,
    paddingBottom: theme.spacing.md,
    backgroundColor: theme.colors.background.card,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.border },
  backButton: {
    paddingVertical: 8,
    paddingRight: 16 },
  backButtonText: {
    fontSize: 16,
    color: theme.colors.brand.primary,
    fontWeight: '500' },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.text.primary },
  headerSpacer: {
    width: 60 },
  content: {
    flex: 1 },
  infoCard: {
    backgroundColor: theme.colors.background.card,
    margin: theme.spacing.md,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.ui.border },
  infoHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing.md },
  spaceName: {
    fontSize: 20,
    fontWeight: 'bold',
    color: theme.colors.text.primary },
  marketName: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    marginTop: 4 },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: theme.borderRadius.md },
  statusText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'capitalize' },
  dateRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.background.secondary,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md },
  dateItem: {
    flex: 1,
    alignItems: 'center' },
  dateLabel: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    marginBottom: 4 },
  dateValue: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text.primary },
  dateDivider: {
    width: 1,
    height: 30,
    backgroundColor: theme.colors.ui.border,
    marginHorizontal: theme.spacing.md },
  daysRemainingBadge: {
    marginTop: theme.spacing.md,
    backgroundColor: `${theme.colors.brand.primary}15`,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: theme.borderRadius.sm,
    alignSelf: 'center' },
  daysRemainingText: {
    fontSize: 14,
    color: theme.colors.brand.primary,
    fontWeight: '600' },
  statsCard: {
    backgroundColor: theme.colors.background.card,
    marginHorizontal: theme.spacing.md,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.ui.border },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text.primary,
    marginBottom: theme.spacing.md },
  statsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginBottom: theme.spacing.md },
  statItem: {
    alignItems: 'center' },
  statValue: {
    fontSize: 28,
    fontWeight: 'bold',
    color: theme.colors.text.primary },
  statLabel: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    marginTop: 4 },
  valueSummary: {
    borderTopWidth: 1,
    borderTopColor: theme.colors.ui.border,
    paddingTop: theme.spacing.md },
  valueRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8 },
  valueLabel: {
    fontSize: 14,
    color: theme.colors.text.secondary },
  valueAmount: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text.primary },
  paymentSection: {
    backgroundColor: theme.colors.background.card,
    marginHorizontal: theme.spacing.md,
    marginBottom: theme.spacing.md,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.md,
    borderWidth: 2,
    borderColor: '#FF9800' },
  paymentHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.sm },
  paymentLabel: {
    fontSize: 14,
    color: theme.colors.text.secondary },
  unpaidBadge: {
    backgroundColor: '#FFF3E0',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: theme.borderRadius.sm },
  unpaidBadgeText: {
    fontSize: 12,
    color: '#E65100',
    fontWeight: '600' },
  paymentAmount: {
    fontSize: 32,
    fontWeight: 'bold',
    color: theme.colors.text.primary,
    marginBottom: theme.spacing.md },
  payNowButton: {
    backgroundColor: '#FF9800',
    paddingVertical: 14,
    borderRadius: theme.borderRadius.md,
    alignItems: 'center' },
  payNowButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8 },
  payNowButtonText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '600' },
  actionsSection: {
    backgroundColor: theme.colors.background.card,
    margin: theme.spacing.md,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.ui.border },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.background.secondary,
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    marginBottom: theme.spacing.sm },
  actionIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#f3f4f6',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12 },
  actionIcon: {
    fontSize: 24,
    marginRight: 12 },
  actionInfo: {
    flex: 1 },
  actionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text.primary },
  actionDescription: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    marginTop: 2 },
  actionChevron: {
    fontSize: 18,
    color: theme.colors.brand.primary,
    fontWeight: '600' },
  dangerSection: {
    backgroundColor: theme.colors.background.card,
    marginHorizontal: theme.spacing.md,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.md,
    borderWidth: 1,
    borderColor: '#FFCDD2' },
  dangerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFEBEE',
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: '#FFCDD2' },
  dangerIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#FFCDD2',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12 },
  dangerIcon: {
    fontSize: 24,
    marginRight: 12 },
  dangerTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#F44336' },
  dangerDescription: {
    fontSize: 12,
    color: '#C62828',
    marginTop: 2 },
  bottomPadding: {
    height: 40 },
  payoutSetupSection: {
    backgroundColor: theme.colors.background.card,
    margin: theme.spacing.md,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.ui.border },
  payoutConnected: {
    backgroundColor: '#E8F5E9',
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md },
  payoutConnectedHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8 },
  payoutConnectedIcon: {
    fontSize: 20,
    color: '#4CAF50',
    marginRight: 8 },
  payoutConnectedTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#2E7D32' },
  payoutConnectedDesc: {
    fontSize: 14,
    color: '#388E3C' },
  payoutNotConnected: {
    backgroundColor: theme.colors.background.secondary,
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md },
  payoutSetupDesc: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    lineHeight: 20,
    marginBottom: theme.spacing.md },
  payoutBenefits: {
    marginBottom: theme.spacing.md },
  payoutBenefit: {
    fontSize: 14,
    color: theme.colors.text.primary,
    marginBottom: 4 },
  connectBankButton: {
    backgroundColor: '#635bff',
    paddingVertical: 14,
    borderRadius: theme.borderRadius.md,
    alignItems: 'center' },
  connectBankButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600' },
  payoutHistorySection: {
    backgroundColor: theme.colors.background.card,
    margin: theme.spacing.md,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.ui.border },
  payoutHistoryCard: {
    backgroundColor: theme.colors.background.secondary,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    marginBottom: theme.spacing.sm },
  payoutHistoryHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing.sm },
  payoutHistoryMarket: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text.primary },
  payoutHistoryDate: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    marginTop: 2 },
  payoutHistoryBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: theme.borderRadius.full },
  payoutHistoryBadgeSuccess: {
    backgroundColor: '#dcfce7' },
  payoutHistoryBadgePending: {
    backgroundColor: '#fef3c7' },
  payoutHistoryBadgeText: {
    fontSize: 12,
    fontWeight: '600' },
  payoutHistoryBadgeTextSuccess: {
    color: '#16a34a' },
  payoutHistoryBadgeTextPending: {
    color: '#92400e' },
  payoutHistoryDetails: {
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.sm,
    padding: theme.spacing.sm },
  payoutHistoryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4 },
  payoutHistoryRowTotal: {
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: theme.colors.ui.borderLight,
    marginBottom: 0 },
  payoutHistoryLabel: {
    fontSize: 13,
    color: theme.colors.text.secondary },
  payoutHistoryLabelBold: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text.primary },
  payoutHistoryValue: {
    fontSize: 13,
    color: theme.colors.text.primary },
  payoutHistoryValueMuted: {
    fontSize: 13,
    color: theme.colors.text.secondary },
  payoutHistoryValueBold: {
    fontSize: 14,
    fontWeight: '700',
    color: '#16a34a' } })
